import { useListSpaceAvailabilities } from '@cocoonspace/sdk-js/domains/availabilities/availabilities'
import type { Availability } from '@cocoonspace/sdk-js/types/availability'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { SpaceHours } from '@cocoonspace/sdk-js/types/spaceHours'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { toast } from 'sonner'
import { PageLoader } from '~/components/page-loader'
import { Price } from '~/components/price'
import { Button } from '~/components/ui/button'
import { useBookingFindOrCreateMutation } from '~/domains/booking/hooks/use-booking-find-or-create.hook'
import { PriceRow } from './desk-booking-stepper'
import { useDeskBookingStore } from './desk-booking.store'
import { useRouterIsLoading } from '~/hooks/use-is-router-loading.hook'
import { useAuth } from '@cocoonspace/shared/domains/auth/hooks/use-auth.hook'
import { useAuthDialogStore } from '@cocoonspace/shared/domains/auth/stores/use-auth-dialog-store.hook'

export const DeskBookingSummaryAvailabilities = ({
	space,
}: { space: Space }) => {
	const formData = useDeskBookingStore.use.formData()

	const spaceHours = useMemo(() => {
		const dayString = dateTimeUtils(formData.day, { locale: 'en' })
			.format('ddd')
			.toLowerCase()

		return {
			start: space.hours[dayString as keyof SpaceHours]?.open?.[0].from,
			end: space.hours[dayString as keyof SpaceHours]?.open?.[0].to,
		}
	}, [formData.day])

	const availableQuery = useListSpaceAvailabilities([
		{
			space: space.id,
			start: `${spaceHours.start}`,
			end: `${spaceHours.end}`,
			day: formData.day,
			type: 'desk',
			qty: formData.nbPeople,
		},
	])

	if (availableQuery.isLoading) {
		return <PageLoader className='h-[200px]' />
	}

	if (availableQuery.isSuccess) {
		return (
			<DeskBookingSummary
				day={formData.day}
				nbPeople={formData.nbPeople}
				space={space}
				payment={availableQuery.data.data[0]}
			/>
		)
	}
}

export const DeskBookingSummary = ({
	day,
	nbPeople,
	space,
	payment,
}: {
	day: string
	nbPeople: number
	space: Space
	payment: Availability
}) => {
	const router = useRouter()
	const { t } = useTranslation()
	const { isAuth } = useAuth()

	const bookingFindOrCreateMutation = useBookingFindOrCreateMutation()

	const isRouterLoading = useRouterIsLoading()
	const openDialog = useAuthDialogStore((state) => state.openDialog)

	const handleBooking = () => {
		if (!isAuth) {
			return openDialog({
				activeTab: 'register',
				errorMsg: t('errors:mustBeAuthToBookDesk'),
			})
		}

		const dayString = dateTimeUtils(day)
			.locale('en')
			.format('ddd')
			.toLocaleLowerCase()
		const dayOpeningHours =
			space.hours[dayString as keyof typeof space.hours]?.open

		bookingFindOrCreateMutation
			.mutateAsync({
				type: 'desk',
				space: space.id,
				day,
				qty: nbPeople,
				start: dayOpeningHours?.[0].from as any,
				end: dayOpeningHours?.[0].to as any,
			})
			.then((res) => {
				router.push(`/booking/${res.booking_id}/payment`)
			})
			.catch((err) => {
				const errorCode = err?.response?.data?.errors?.[0].code as
					| string
					| undefined

				toast.error(
					t([
						`errors:context.booking.${errorCode}`,
						`errors:api.${errorCode}`,
						'errors:generic',
					]),
				)
			})
	}

	return (
		<div className='-mt-2 flex w-full flex-col gap-4 px-6 pb-6'>
			<div className='flex flex-col gap-2'>
				<div className='font-bold capitalize'>
					{dateTimeUtils(day).customFormat('dateFull')}
				</div>

				{!!payment.price_unit && (
					<PriceRow>
						<div className='flex flex-row items-center gap-1 text-sm leading-none'>
							<Price
								price={payment.price_unit}
								className='font-normal'
							/>{' '}
							<span>x</span>
							{t('space:sidebarDesk.person', {
								count: payment.billed_qty,
							})}
						</div>

						<Price
							price={payment.price}
							className='font-normal text-sm leading-none'
							taxType='ht'
							taxClassName='mb-0.5'
						/>
					</PriceRow>
				)}

				{!!payment.booking_fees && (
					<PriceRow>
						<div className='flex flex-row items-center gap-1 text-sm leading-none'>
							{t('space:sidebarDesk.bookingDialog.bookingFees')}
						</div>

						<Price
							price={payment.booking_fees}
							className='font-normal text-sm leading-none'
							taxType='ht'
						/>
					</PriceRow>
				)}

				{!!payment.booking_fees && !!payment.price && (
					<PriceRow>
						<div className='flex flex-row items-center gap-1 leading-none'>
							{t('space:sidebarDesk.bookingDialog.totalHt')}
						</div>

						<Price
							price={payment.booking_fees + payment.price}
							className='text-xl leading-none'
							taxType='ht'
						/>
					</PriceRow>
				)}
			</div>

			<Button
				disabled={bookingFindOrCreateMutation.isLoading || isRouterLoading}
				variant='gradient'
				onClick={handleBooking}
			>
				{t('actions:book.btnLabel')}
			</Button>
		</div>
	)
}
